import type { CheckAdjudication } from '@privisy/api-types';
import { ComplianceCheck } from '../../utils/types';
import { SEVERITY_ORDER } from '../../designTokens';
import { crosscheckState } from './crosscheck/state';

/**
 * The three groups of "What to fix" (#1660), in display order:
 * - `fix`: failing checks (non_compliant, error), statutory gaps or stage failures.
 * - `recommended`: a warning that is a real gap worth fixing.
 * - `confirm`: a warning whose duty the scan cannot observe ("no action needed if
 *   it doesn't apply"). Set by the catalog's `confirmApplies` (#1657), or by an
 *   `advisory` warning, which #1653 already grouped this way. A report stored
 *   before either flag puts every warning under `recommended`.
 */
export type FixGroup = 'fix' | 'recommended' | 'confirm';

export const FIX_GROUP_ORDER: readonly FixGroup[] = ['fix', 'recommended', 'confirm'];

export const FIX_GROUP_LABEL: Record<FixGroup, string> = {
  fix: 'Fix these',
  recommended: 'Recommended',
  confirm: 'Confirm it applies to you',
};

export const CONFIRM_GROUP_NOTE = "no action needed if it doesn't";

export function fixGroupOf(check: ComplianceCheck): FixGroup | null {
  // Status casing has never been guaranteed on stored reports.
  const status = check.status?.toLowerCase();
  if (status === 'non_compliant' || status === 'error') return 'fix';
  if (status !== 'warning') return null;
  // Assumption: `advisory` keeps its #1653 placement here even without
  // `confirmApplies`, so no warning moves back into the findings.
  return check.confirmApplies || check.advisory ? 'confirm' : 'recommended';
}

const bySeverity = (a: ComplianceCheck, b: ComplianceCheck) =>
  (SEVERITY_ORDER[a.severity?.toLowerCase()] ?? 99) - (SEVERITY_ORDER[b.severity?.toLowerCase()] ?? 99);

/** Each group sorted worst first; passing and skipped checks are dropped. */
export function groupIssues(checks: ComplianceCheck[]): Record<FixGroup, ComplianceCheck[]> {
  const groups: Record<FixGroup, ComplianceCheck[]> = { fix: [], recommended: [], confirm: [] };
  for (const c of checks) {
    const group = fixGroupOf(c);
    if (group) groups[group].push(c);
  }
  for (const g of FIX_GROUP_ORDER) groups[g].sort(bySeverity);
  return groups;
}

/**
 * The index row's badge, mirroring the card's (also the locked teaser's
 * findings list). Title case here; the badge CSS uppercases it.
 */
export function fixBadge(check: ComplianceCheck, group: FixGroup): { modifier: string; text: string } {
  if (group === 'confirm') return { modifier: 'confirm', text: 'Check' };
  if (group === 'recommended') return { modifier: 'warning', text: 'Warning' };
  const severity = check.severity?.toLowerCase() ?? '';
  const text = severity.charAt(0).toUpperCase() + severity.slice(1);
  // An `error` card has no scored severity stripe (IssueCard), so its row badge doesn't either.
  return { modifier: check.status === 'non_compliant' ? severity : 'warning', text };
}

/** DOM id of an issue card, the target of its fix index row. Check ids are UUIDs. */
export function issueCardId(check: Pick<ComplianceCheck, 'id'>): string {
  return `issue-${check.id}`;
}

/**
 * The index row's AI Crosscheck tag, from the same states the card shows (#1659):
 * agreement (`confirmed`, or an enforced correction to a failure) reads Confirmed;
 * an unapplied disagreement reads Disputed. A corrected pass never reaches this list.
 */
export function crosscheckTag(adjudication: CheckAdjudication | undefined): 'Confirmed' | 'Disputed' | null {
  switch (crosscheckState(adjudication)) {
    case 'confirmed':
    case 'corrected-fail':
      return 'Confirmed';
    case 'disputed-failed':
    case 'disputed-passed':
      return 'Disputed';
    default:
      return null;
  }
}
